import {redirectToAuth, resolveRpcUrl} from './utils/locationUtils';
import {useRef} from 'react';
import promiseRetry from 'promise-retry';
import {
  GetStreamReportsResponse,
  Report,
  ReportStats,
  ReportStreamBpmLogsResponse,
} from './types/Api/Reports';

type RetryOptions = Parameters<typeof promiseRetry>[0];

export type PulsoidClientOptions = {
  publicRpc?: boolean;
  noAuthRedirect?: boolean;
  retryOptions?: RetryOptions;
};

export const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  retries: 3,
  factor: 2,
  minTimeout: 1 * 1000,
  maxTimeout: 5 * 1000,
};

class PulsoidClient {
  private isPublicRpc = false;
  private noAuthRedirect = false;
  private retryOptions: RetryOptions | undefined = undefined;

  constructor({
    publicRpc = false,
    noAuthRedirect = false,
    retryOptions,
  }: PulsoidClientOptions = {}) {
    this.isPublicRpc = publicRpc;
    this.noAuthRedirect = noAuthRedirect;
    this.retryOptions = retryOptions;
    // @ts-ignore
    if (import.meta.env.VITE_APP_PLAY_SESSION) {
      // @ts-ignore
      this.setCookie('PLAY_SESSION', import.meta.env.VITE_APP_PLAY_SESSION, 10);
    }
  }
  setCookie(cname: string, cvalue: string, exdays: number) {
    const d = new Date();
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
    let expires = 'expires=' + d.toUTCString();
    document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
  }

  async performRequest(method: string, params: any) {
    let url = resolveRpcUrl(this.isPublicRpc);
    var options = {
      method: 'POST',
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: method,
        params: params,
        id: '1',
      }),
      headers: {
        'Content-Type': 'application/json',
        'x-rpc-method': method,
      },
      credentials: 'include' as RequestCredentials,
    };

    let response = await fetch(url, options);
    let json = await response.json();
    if (json.error) {
      let error = json.error;
      if (error.code === -33422) {
        if (!this.noAuthRedirect) {
          redirectToAuth({isReplace: true});
          error = new Error(`not authorized`);
          error.name = NotAuthorizedError;
          throw error;
        } else {
          return null;
        }
      }
      throw new JsonRpcException(error);
    }
    return json.result;
  }

  toPromise(method: string, params: any): Promise<any> {
    if (this.retryOptions) {
      return promiseRetry(
        (retry) =>
          this.performRequest(method, params).catch((err) => retry(err)),
        this.retryOptions
      );
    }

    return this.performRequest(method, params);
  }

  captureWithSentry(error, params) {
    // Sentry.withScope(scope => {
    //     scope.setTag("params", JSON.stringify(params));
    //     Sentry.captureException(error);
    // });
  }

  getUserInfo(): Promise<UserInfo> {
    return this.toPromise('getUserInfo', []);
  }

  useCoupon(coupon) {
    return this.toPromise('useCoupon', {coupon});
  }

  getPremiumInfo(): Promise<PremiumInfo> {
    return this.toPromise('getPremiumInfo', []);
  }

  // from to should be in 2020-08-15T11:17:27.618Z UTC format
  getStatistics(from?: string, to?: string): Promise<ReportStats> {
    return this.toPromise('getStatistics', {from, to});
  }

  activateTrial() {
    return this.toPromise('activateTrial', {
      originUrl: window.location.pathname,
    });
  }

  getAllWidgets(): Promise<Widget[]> {
    return this.toPromise('getAllWidgets', []);
  }

  getWidget(widgetId: string) {
    return this.toPromise('getWidget', {widgetId});
  }

  updateWidget(widgetId: string, configuration) {
    return this.toPromise('updateWidget', {widgetId, configuration});
  }

  getPredefinedWidgets() {
    return this.toPromise('getPredefinedWidgets', []);
  }

  deleteWidget(widgetId: string) {
    return this.toPromise('deleteWidget', {widgetId});
  }

  addWidget(predefinedWidgetId: string, name: string) {
    return this.toPromise('addWidget', {predefinedWidgetId, name});
  }

  reportPageOpened(page) {
    return this.toPromise('reportBi', {tpe: 'PageOpened', event: {page}});
  }

  reportUPMSideBarOpened() {
    return this.toPromise('reportBi', {tpe: 'UPMSideBarOpened', event: {}});
  }

  reportUPMSideBarClosed() {
    return this.toPromise('reportBi', {tpe: 'UPMSideBarClosed', event: {}});
  }

  reportUPMSideBarCompleted() {
    return this.toPromise('reportBi', {tpe: 'UPMSideBarCompleted', event: {}});
  }

  reportRecommendedWidgetAdded(predefinedWidgetId: string) {
    return this.toPromise('reportBi', {
      tpe: 'RecommendedWidgetAdded',
      event: {predefinedWidgetId},
    });
  }

  reportFeatureDescriptionOpened(featureDescriptionId: string) {
    return this.toPromise('reportBi', {
      tpe: 'FeatureDescriptionOpened',
      event: {featureDescriptionId, uri: document.location.href},
    });
  }

  updateChannel(channel: string) {
    return this.toPromise('updateChannel', {channel});
  }

  generateTemporaryPassword() {
    return this.toPromise('generateTemporaryPassword', []);
  }

  getFeatureDescription(id: string): Promise<GetFeatureDescriptionResponse> {
    return this.toPromise('getFeatureDescription', {id});
  }

  getUpdates() {
    return this.toPromise('getUpdates', {});
  }

  async uploadFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch('/v2/api/file', {
        method: 'POST',
        credentials: 'include', // same as axios withCredentials
        // Don't set the Content-Type header explicitly when sending FormData;
        // the browser will set the correct boundary automatically.
        body: formData,
      });

      if (!response.ok) {
        throw new Error(
          `Request failed with status ${response.status} - ${response.statusText}`
        );
      }

      // Assuming the response is JSON containing { fileUrl: string }
      const data = (await response.json()) as {fileUrl: string};
      return data.fileUrl;
    } catch (e) {
      console.error('error', e);
      throw e;
    }
  }

  addFileToGallery(name: string, url: string, type: string) {
    return this.toPromise('addFileToGallery', {name, url, type});
  }

  getGallery() {
    return this.toPromise('getGallery', {});
  }

  getPulsoidGallery() {
    return this.toPromise('getPulsoidGallery', {});
  }

  removeFileFromGallery(fileId: string) {
    return this.toPromise('removeFileFromGallery', {fileId});
  }

  getMetaWebHooks() {
    return this.toPromise('getMetaWebHooks', {}).then(
      (res) => res.metaWebHooks
    );
  }

  getAllWebHooks() {
    return this.toPromise('getAllWebHooks', {}).then((res) => res.webHooks);
  }

  addWebHook(name: string, metaId: string) {
    return this.toPromise('addWebHook', {name, metaId});
  }

  getWebHook(id: string) {
    return this.toPromise('getWebHook', {id});
  }

  deleteWebHook(id: string) {
    return this.toPromise('deleteWebHook', {id});
  }

  changeWebHookStatus(id: string, status: string) {
    return this.toPromise('changeWebHookStatus', {id, status});
  }

  updateWebHook(id: string, triggerBpm, triggerWindow, configuration) {
    return this.toPromise('updateWebHook', {
      id,
      triggerBpm,
      triggerWindow,
      configuration,
    });
  }

  getYearlyReport() {
    return this.toPromise('getYearlyReport', {});
  }

  getYearlyReportById(id: string) {
    if (this.isPublicRpc) {
      return this.toPromise('getReport', {id});
    } else {
      console.error('getYearlyReportById is not available for non public rpc');
    }
  }

  executeTestWebHook(
    id: string,
    bpm: number
  ): Promise<IntegrationExecutionResult> {
    return this.toPromise('executeTestWebHook', {id, bpm});
  }

  getSelfCreatedAccessTokens() {
    return this.toPromise('getSelfCreatedAccessTokens', {});
  }

  createAccessToken(alias) {
    return this.toPromise('createAccessToken', {alias});
  }

  revokeAccessToken(token: string) {
    return this.toPromise('revokeAccessToken', {token});
  }

  cancelSubscription() {
    return this.toPromise('cancelSubscription', {});
  }

  getConnectedClients() {
    return this.toPromise('getConnectedClients', {}).then((res) => res.clients);
  }

  revokeTokens(clientId) {
    return this.toPromise('revokeTokens', {clientId});
  }

  getReferralStatistics() {
    return this.toPromise('getReferralStatistics', {});
  }

  // stream reports rpc start
  getStreamReports(page: {
    page: number;
    size: number;
  }): Promise<GetStreamReportsResponse> {
    return this.toPromise('getStreamReports', {page});
  }

  getStreamReport(id: string): Promise<{report: Report}> {
    return this.toPromise('getStreamReport', {id});
  }

  getStreamReportBySlug(slug: string): Promise<{report: Report}> {
    return this.toPromise('getStreamReportBySlug', {slug});
  }

  getStreamReportBpmLogs(id: string): Promise<ReportStreamBpmLogsResponse> {
    return this.toPromise('getStreamReportBpmLogs', {id});
  }
  // stream reports rpc end

  disconnectConnectedAccount(platform: 'Twitch' | 'Youtube') {
    return this.toPromise('disconnectConnectedAccount', {platform});
  }

  getAutomatedClipsSettings() {
    return this.toPromise('getAutomatedClipsSettings', {});
  }

  updateAutomatedClipSettings(status: string, thresholdBpm: number) {
    return this.toPromise('updateAutomatedClipSettings', {
      settings: {status, thresholdBpm},
    });
  }

  validateCoupon(coupon: string): Promise<ValidateCouponResponse> {
    return this.toPromise('validateCoupon', {coupon});
  }

  redeemCoupon(coupon: string): Promise<RedeemCouponResponse> {
    return this.toPromise('redeemCoupon', {coupon});
  }

  getCoupons() {
    return this.toPromise('getCoupons', {});
  }

  createCouponCheckoutSession(
    request: CreateCouponCheckoutSessionRequest
  ): Promise<CreateCouponCheckoutSessionResponse> {
    return this.toPromise('createCouponCheckoutSession', request);
  }

  getPurchasedCoupon(
    request: GetPurchasedCouponRequest
  ): Promise<GetPurchasedCouponResponse> {
    return this.toPromise('getPurchasedCoupon', request);
  }

  getCouponCheckoutProducts(): Promise<GetCheckoutProductsResponse> {
    return this.toPromise('getCouponCheckoutProducts', {});
  }

  getPartner(slug: string): Promise<GetPartnerResponse> {
    return this.toPromise('getPartner', {slug});
  }

  cloneWidget(sourceWidgetId: string): Promise<CloneWidgetResponse> {
    return this.toPromise('cloneWidget', {sourceWidgetId});
  }

  getBroPlanCheckoutProducts(): Promise<GetCheckoutProductsResponse> {
    return this.toPromise('getBroPlanCheckoutProducts', {});
  }

  getSidebarAdvertisements(): Promise<SidebarAdvertisement[]> {
    return this.toPromise('getSidebarAdvertisements', {}).then(
      (x) => x.advertisements
    );
  }

  hasTwitchTokenByScope(scope: string[]): Promise<boolean> {
    return this.toPromise('hasTwitchTokenByScope', {scope}).then(
      (x) => x.hasToken
    );
  }

  publicStartPrediction(
    accessToken: string,
    title: String,
    outcomes: string[]
  ): Promise<Prediction> {
    if (this.isPublicRpc) {
      return this.toPromise('startPrediction', {
        accessToken,
        title,
        outcomes,
      }).then((x) => x.prediction);
    } else {
      throw new Error(
        'publicStartPrediction is not available for non public rpc'
      );
    }
  }

  publicResolvePrediction(
    accessToken: string,
    predictionId: string,
    winningOutcomeId: string
  ) {
    if (this.isPublicRpc) {
      return this.toPromise('resolvePrediction', {
        accessToken,
        predictionId,
        winningOutcomeId,
      });
    } else {
      throw new Error(
        'publicResolvePrediction is not available for non public rpc'
      );
    }
  }

  createOrGetTwitchExtensionConfiguration(): Promise<GetTwitchExtensionConfigurationResponse> {
    return this.toPromise('createOrGetTwitchExtensionConfiguration', {});
  }

  getTwitchExtensionConfiguration(): Promise<GetTwitchExtensionConfigurationResponse> {
    return this.toPromise('getTwitchExtensionConfiguration', {});
  }

  calculateTwitchExtensionConnectAction(
    jwtToken: string
  ): Promise<CalculateTwitchExtensionConnectActionResponse> {
    return this.toPromise('calculateTwitchExtensionConnectAction', {jwtToken});
  }

  connectTwitchAccountByTwitchExtensionToken(jwtToken: string) {
    return this.toPromise('connectTwitchAccountByTwitchExtensionToken', {
      jwtToken,
    });
  }

  async updateTwitchExtensionVisibility(
    request: TwitchExtensionUpdateVisibilityRequest
  ): Promise<
    Result<GetTwitchExtensionConfigurationResponse, UpdateVisibilityError>
  > {
    try {
      let response = await this.toPromise(
        'updateTwitchExtensionVisibility',
        request
      );
      return {ok: true, value: response};
    } catch (error) {
      if (error instanceof JsonRpcException) {
        let reason = error.data.reason as string;
        if (reason === 'TwitchTokenWithRequiredScopeMissing') {
          return {
            ok: false,
            error: {
              reason: 'TwitchTokenWithRequiredScopeMissing',
              scope: error.data.scope,
            },
          };
        } else {
          throw error;
        }
      }
      throw error;
    }
  }

  async updateTwitchExtensionStreamDelay(
    streamDelayMilliseconds
  ): Promise<GetTwitchExtensionConfigurationResponse> {
    return this.toPromise('updateTwitchExtensionStreamDelay', {
      streamDelayMilliseconds,
    });
  }

  async addOrUpdateTwitchExtensionV1LayoutContainer(
    container: Container
  ): Promise<GetTwitchExtensionConfigurationResponse> {
    return this.toPromise('addOrUpdateTwitchExtensionV1LayoutContainer', {
      container,
    });
  }

  async getDashboardToken(): Promise<GetDashboardTokenResponse> {
    return this.toPromise('getDashboardToken', {});
  }
}

export type GetDashboardTokenResponse = {
  token: string;
};

export type Result<T, E = Error> = {ok: true; value: T} | {ok: false; error: E};

export type UpdateVisibilityError = {
  reason: 'TwitchTokenWithRequiredScopeMissing';
  scope?: string;
};

export type TwitchExtensionUpdateVisibilityRequest = {
  everyone?: boolean;
  followers?: boolean;
  subscribers?: boolean;
};

export type TwitchExtensionConfiguration = {
  id: string;
  visibility: {
    followers: boolean;
    subscribers: boolean;
  };
  streamDelayMilliseconds: number;
  v1Layout?: V1Layout;
};

export type Container = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  widgetId: string;
};

export type V1Layout = {
  containers: Container[];
};

export type GetTwitchExtensionConfigurationResponse = {
  configuration: TwitchExtensionConfiguration;
};

export type TwitchExtensionConnectAction =
  | 'AlreadyConnected'
  | 'ConnectTwitchAccountAndCreateConfiguration'
  | 'RewireTwitchChannelAndCreateConfiguration';

export type CalculateTwitchExtensionConnectActionResponse = {
  action: TwitchExtensionConnectAction;
  channelName?: string;
  twitchProfileEmail?: string;
  email: string;
  hasConfiguration: boolean;
};

export type SidebarAdvertisement = {
  link?: string;
  featureDescriptionId?: string;
  imageUrl: string;
};

export type PartnerFavoriteFeature = {
  imageUrl: string;
  descriptionId: string;
  title: string;
};
export type PartnerFavoriteWidget = {
  widgetId: string;
};

export type PartnerSocial = {
  discordUrl?: string;
  twitterUrl?: string;
  youtubeUrl?: string;
  twitchUrl?: string;
  tiktokUrl?: string;
  instagramUrl?: string;
};

export type Partner = {
  id: string;
  joinedAt: string; // iso date
  name: string;
  social: PartnerSocial;
  bannerUrl?: string;
  avatarUrl: string;
  feedback: string;
  favoriteWidgets: PartnerFavoriteWidget[];
  favoriteFeatures: PartnerFavoriteFeature[];
  videoUrl: string;
};

export type PartnerStatistics = {
  max: number;
  min: number;
  avg: number;
  p95: number;
  broadcasted: string; // duration
};

export type GetPartnerResponse = {
  partner?: Partner;
  statistics?: PartnerStatistics;
  twitchOnline: boolean;
};

export type CouponReason =
  | 'TrialExtended'
  | 'SubscriptionExtended'
  | 'TrialApplied'
  | 'CouponNotApplicableForLifetime'
  | 'CouponNotFound'
  | 'CouponAlreadyUsedByProfile'
  | 'CouponNotApplicableForThisProfile'
  | 'CouponExhaustedOrExpired'
  | 'Unknown'; // FE specific reason for edge cases

export type ValidateCouponResponse = {
  applicable: boolean;
  /**
   * exists only if applicable
   * https://momentjs.com/docs/#/durations/creating/
   */
  duration?: string;
  reason: CouponReason;
};
export type RedeemCouponResponse = {
  applied: boolean;
  /**
   * exists only if applied
   * https://momentjs.com/docs/#/durations/creating/
   */
  duration?: string;
  reason: CouponReason;
};

export type CheckoutProductId = string;
export type CouponCheckoutSessionId = string;

export type Discount = {
  price: Money;
};

export type Money = {
  amount: string;
  currency: string;
};

export type ProviderProductConfig = {
  override?: string;
  productId: string;
};

export type CheckoutProduct = {
  id: CheckoutProductId;
  provider: ProviderProductConfig;
  price: Money;
  discount?: Discount;
  title: string;
  description?: string;
};

export type GetCheckoutProductsResponse = {
  products: CheckoutProduct[];
  providerVendorId: string;
  sandbox: boolean;
};

export type CreateCouponCheckoutSessionRequest = {
  /**
   * taken from CheckoutProduct.id
   */
  productId: CheckoutProductId;
};

export type CreateCouponCheckoutSessionResponse = {
  checkoutSessionId: CouponCheckoutSessionId;
};

export type GetPurchasedCouponRequest = {
  checkoutSessionId: CouponCheckoutSessionId;
};

export type GetPurchasedCouponResponse = {
  coupon?: string;
  duration?: string;
};

export const NotAuthorizedError = 'NotAuthorized';

export default PulsoidClient;

export const usePulsoidClient = (options?: PulsoidClientOptions) => {
  const client = useRef(new PulsoidClient(options));

  return client.current;
};

export type IntegrationExecutionResult = {
  success: boolean;
  details?: string;
};

export type CloneWidgetResponse = {
  clonedWidgetId: string;
};

type GetFeatureDescriptionResponse = {
  id: string;
  title: string;
  content: string;
};

export type UserInfo = {
  id: string;
  email: string;
  ramielReference: string;
  endpoints: {
    write: string;
    listen: string;
    average: string;
    ramielToken: string;
  };
  rssReference: string;
  isAdmin: boolean;
  isCompanyManager: boolean;
  isUserStreamed: boolean;
  isUserLoggedInMobile: boolean;
  premium: PremiumInfo;
  referralUrl: string;
  isTwitchConnected: boolean;
};

export type PremiumInfo = {
  id: string;
  isPremium: boolean;
  isHadTrial: boolean;
  isSubscribed: boolean;
  description: string;
  isLifetime: boolean;
  isPastDue: boolean;
  updateUrl?: string;
};

export type PredictionOutcome = {
  id: string;
  title: string;
  users: number;
  channel_points: number;
  color: string;
};

export type Prediction = {
  id: string;
  broadcaster_id: string;
  broadcaster_name: string;
  broadcaster_login: string;
  title: string;
  outcomes: PredictionOutcome[];
  prediction_window: number;
  status: 'ACTIVE' | 'RESOLVED' | 'CANCELED' | 'LOCKED'; // Added common status values
  created_at: string;
};

export class JsonRpcException extends Error {
  data: any;
  error: any;

  constructor(error: any) {
    super(error.message);
    this.data = error.data;
    this.error = error;
  }

  toString() {
    return `JsonRpcException ${JSON.stringify(this.error)}`;
  }
}

export type WidgetNote = {
  name: string;
  description: string;
};

export type Widget = {
  metaId: string;
  predefinedWidgetId: string;
  id: string;
  profileId: string;
  createdAt: string;
  premium: boolean;
  note: WidgetNote;
  configuration: any;
  ramielUrl: string;
  premiumUpdate: boolean;
  token: string;
};
